/* ============================================================================
   Choomchon realtime chat client
   ----------------------------------------------------------------------------
   One WebSocket per tab to  /realtime/  (Django Channels + Redis).
   The server is the only source of truth: nothing chat-related is kept in
   localStorage, so there is no second copy of a message to show twice.

   Sending:   message gets a random clientId, appears at once with a clock icon,
              is sent on the socket, and becomes ✓✓ when the server acks it
              (= stored in the database). No ack in 8 s → the same clientId is
              posted over HTTP; the server ignores the repeat if it already has it.
   Receiving: "message" events are merged by server id / clientId, so an echo of
              your own message replaces the pending bubble instead of adding one.
   Reading:   the thread's peerReadId says which of my messages the other person
              has seen (coloured ✓✓); everything above it is grey ✓✓.
   Offline:   exponential reconnect with jitter; while disconnected the inbox and
              the open chat are polled over HTTP so nothing is missed.
   ========================================================================== */

const hasWindow = typeof window !== "undefined";
const ACK_TIMEOUT = 8000;
const PING_EVERY = 25000;
const PONG_TIMEOUT = 10000;
const POLL_OFFLINE = 15000;   // inbox refresh when the socket is down
const POLL_CHAT = 4000;       // open conversation refresh when the socket is down

const rid = () => {
  const a = new Uint8Array(12);
  (window.crypto || window.msCrypto).getRandomValues(a);
  return "c" + Array.from(a, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 22);
};

export function realtimeUrl(apiBase) {
  const explicit = (process.env.REACT_APP_WS_URL || "").trim();
  // npm start: live chat goes through the dev proxy like the API does
  if (explicit && process.env.NODE_ENV !== "development") return explicit.replace(/\/?$/, "/");
  if (/^https?:\/\//i.test(apiBase)) {
    const u = new URL(apiBase);
    return `${u.protocol === "https:" ? "wss:" : "ws:"}//${u.host}/realtime/`;
  }
  const loc = window.location;
  return `${loc.protocol === "https:" ? "wss:" : "ws:"}//${loc.host}/realtime/`;
}

export function createChat({ api, apiBase }) {
  const C = {
    me: null,
    status: "offline",          // offline | connecting | online
    threads: new Map(),         // id -> thread
    msgs: new Map(),            // id -> { list, hasMore, loaded, loading }
    typing: new Map(),          // thread id -> until (ms)
    helloUnread: 0,
    threadsLoaded: false,
    listeners: new Set(),
    onIncoming: null,           // (message, thread) => void
    ws: null, attempts: 0, timers: {}, outbox: new Map(), visibleThread: null,
  };

  C.httpOk = true;
  C.markHttp = (ok) => { if (C.httpOk !== ok) { C.httpOk = ok; emit(); } };
  // Show "Reconnecting…" only when messages really can't get through.
  C.isOffline = () => (typeof navigator !== "undefined" && navigator.onLine === false) || !C.httpOk;
  C.isLive = () => C.status === "online";
  const emit = () => C.listeners.forEach((f) => { try { f(); } catch (e) { console.error(e); } });
  C.subscribe = (f) => { C.listeners.add(f); return () => C.listeners.delete(f); };

  /* ─── thread helpers ─────────────────────────────────────────────────── */
  function upsertThread(patch) {
    if (!patch || !patch.id) return null;
    const prev = C.threads.get(patch.id) || {};
    const th = { ...prev, ...patch };
    // events carry both sides' counters; pick mine
    if ("sellerUnread" in patch) {
      const seller = th.sellerId === C.me;
      th.unread = seller ? patch.sellerUnread : patch.buyerUnread;
      th.peerReadId = Math.max(prev.peerReadId || 0, seller ? patch.buyerReadId : patch.sellerReadId);
      th.myReadId = Math.max(prev.myReadId || 0, seller ? patch.sellerReadId : patch.buyerReadId);
      delete th.sellerUnread; delete th.buyerUnread; delete th.sellerReadId; delete th.buyerReadId;
    }
    C.threads.set(th.id, th);
    return th;
  }
  C.threadList = () => [...C.threads.values()].filter((t) => t.lastMessageId > 0 || t.local)
    .sort((a, b) => (b.lastAt || 0) - (a.lastAt || 0));
  C.thread = (id) => C.threads.get(id) || null;
  C.unreadTotal = () => (C.threadsLoaded
    ? [...C.threads.values()].reduce((a, t) => a + (t.unread || 0), 0)
    : C.helloUnread);
  C.unreadThreads = () => [...C.threads.values()].filter((t) => t.unread > 0).length;
  C.isTyping = (id) => (C.typing.get(id) || 0) > Date.now();
  C.dealtWith = (key) => !!key && key !== C.me && [...C.threads.values()].some((t) =>
    t.lastMessageId > 0 && ((t.sellerId === key && t.buyerId === C.me) || (t.buyerId === key && t.sellerId === C.me)));

  /* ─── message list helpers ───────────────────────────────────────────── */
  const box = (id) => { if (!C.msgs.has(id)) C.msgs.set(id, { list: [], hasMore: false, loaded: false, loading: false }); return C.msgs.get(id); };
  function mergeMessage(m) {
    const b = box(m.thread);
    const i = b.list.findIndex((x) => (m.id && x.id === m.id) || (m.clientId && x.clientId === m.clientId));
    const rec = { ...m, status: "sent" };
    if (i >= 0) b.list[i] = { ...b.list[i], ...rec };
    else {
      // stored messages ordered by id; pending ones (no id yet) stay at the bottom
      const firstPending = b.list.findIndex((x) => !x.id);
      const stored = firstPending < 0 ? b.list : b.list.slice(0, firstPending);
      const pending = firstPending < 0 ? [] : b.list.slice(firstPending);
      let k = stored.length;
      while (k > 0 && stored[k - 1].id > m.id) k--;
      stored.splice(k, 0, rec);
      b.list = [...stored, ...pending];
    }
    if (m.clientId) { C.outbox.delete(m.clientId); clearTimeout(C.timers["ack" + m.clientId]); }
  }
  C.messages = (id) => {
    const b = box(id);
    // called during render: start the fetch after React finishes this render
    if (!b.loaded && !b.loading && !b.queued && C.me) { b.queued = true; Promise.resolve().then(() => { b.queued = false; C.load(id); }); }
    return b;
  };

  C.load = async (id) => {
    const b = box(id); b.loading = true; emit();
    try {
      const d = await api(`/threads/${encodeURIComponent(id)}/messages/?limit=50`);
      // Merge, don't replace: a message sent or received while this request was
      // in flight must neither disappear nor show twice.
      const server = (d.messages || []).map((m) => ({ ...m, status: "sent" }));
      const ids = new Set(server.map((m) => m.id));
      const cids = new Set(server.map((m) => m.clientId).filter(Boolean));
      const maxId = server.length ? server[server.length - 1].id : 0;
      const newer = b.list.filter((x) => x.id && !ids.has(x.id) && x.id > maxId);
      const pending = b.list.filter((x) => !x.id && !cids.has(x.clientId));
      b.list = [...server, ...newer, ...pending];
      b.hasMore = !!d.hasMore; b.loaded = true;
      if (d.thread) upsertThread(d.thread);
    } catch (e) { b.error = true; }
    b.loading = false; emit();
  };
  C.loadOlder = async (id) => {
    const b = box(id); const first = b.list.find((x) => x.id);
    if (!first || b.loading || !b.hasMore) return;
    b.loading = true; emit();
    try {
      const d = await api(`/threads/${encodeURIComponent(id)}/messages/?limit=50&before=${first.id}`);
      b.list = [...(d.messages || []).map((m) => ({ ...m, status: "sent" })), ...b.list];
      b.hasMore = !!d.hasMore;
    } catch (e) { /* keep what we have */ }
    b.loading = false; emit();
  };
  async function catchUp(id) {
    const b = C.msgs.get(id);
    if (!b || !b.loaded) return;
    const last = [...b.list].reverse().find((x) => x.id);
    try {
      const d = await api(`/threads/${encodeURIComponent(id)}/messages/?limit=200${last ? "&after=" + last.id : ""}`);
      const known = new Set(b.list.map((x) => x.id).filter(Boolean));
      (d.messages || []).forEach(mergeMessage);
      if (d.thread) upsertThread(d.thread);
      C.markHttp(true);
      const fresh = (d.messages || []).filter((m) => !known.has(m.id) && m.from !== C.me);
      if (fresh.length && C.visibleThread === id) C.markRead(id);
      emit();
    } catch (e) { if (!e || !e.status) C.markHttp(false); }
  }

  C.syncThreads = async () => {
    if (!C.me) return;
    try {
      const d = await api("/threads/");
      const seen = new Set(), wasLoaded = C.threadsLoaded, arrivals = [];
      (d.threads || []).forEach((t) => {
        seen.add(t.id);
        const prev = C.threads.get(t.id);
        // Without the socket, new messages are discovered here: alert like a live one would.
        if (wasLoaded && t.lastBy && t.lastBy !== C.me && t.lastMessageId > ((prev && prev.lastMessageId) || 0)) arrivals.push(t);
        upsertThread({ ...t, local: false });
      });
      for (const [id, t] of C.threads) if (!seen.has(id) && !t.local) C.threads.delete(id);
      C.threadsLoaded = true; C.markHttp(true); emit();
      if (C.status !== "online" && C.onIncoming) arrivals.forEach((t) => {
        if (C.visibleThread === t.id) catchUp(t.id);
        C.onIncoming({ id: t.lastMessageId, thread: t.id, from: t.lastBy, text: t.lastText, at: t.lastAt }, C.threads.get(t.id));
      });
    } catch (e) { if (!e || !e.status) C.markHttp(false); }
  };

  /* ─── starting a conversation ────────────────────────────────────────── */
  C.start = async (listingId) => {
    const th = await api("/threads/", { method: "POST", body: JSON.stringify({ listingId }) });
    const cur = C.threads.get(th.id);
    upsertThread({ ...th, local: !(cur && cur.lastMessageId > 0) && !(th.lastMessageId > 0) });
    emit();
    return th.id;
  };
  C.fetchThread = async (id) => {
    try { upsertThread(await api(`/threads/${encodeURIComponent(id)}/`)); emit(); return true; }
    catch (e) { return false; }
  };

  /* ─── sending ────────────────────────────────────────────────────────── */
  C.send = (threadId, text) => {
    text = String(text || "").trim().slice(0, 2000);
    if (!text || !C.me) return;
    const clientId = rid();
    const m = { id: null, clientId, thread: threadId, from: C.me, text, at: Date.now(), status: "sending" };
    box(threadId).list.push(m);
    const th = C.threads.get(threadId);
    if (th) upsertThread({ id: threadId, lastText: text.slice(0, 140), lastAt: m.at, lastBy: C.me, local: th.local });
    C.outbox.set(clientId, m);
    transmit(m);
    emit();
  };
  C.retry = (clientId) => {
    const b = [...C.msgs.values()].find((x) => x.list.some((m) => m.clientId === clientId));
    const m = b && b.list.find((x) => x.clientId === clientId);
    if (!m) return;
    m.status = "sending"; C.outbox.set(clientId, m); transmit(m); emit();
  };
  C.discard = (clientId) => {
    C.outbox.delete(clientId);
    for (const b of C.msgs.values()) b.list = b.list.filter((m) => m.clientId !== clientId || m.id);
    emit();
  };
  function transmit(m) {
    clearTimeout(C.timers["ack" + m.clientId]);
    if (C.ws && C.ws.readyState === 1) {
      C.ws.send(JSON.stringify({ type: "send", thread: m.thread, text: m.text, clientId: m.clientId }));
      C.timers["ack" + m.clientId] = setTimeout(() => httpSend(m), ACK_TIMEOUT);
    } else httpSend(m);
  }
  async function httpSend(m) {
    if (!C.outbox.has(m.clientId)) return;
    // Same clientId on every attempt, so a retry can never create a second copy.
    // Network errors and 5xx are retried automatically before showing "Not sent".
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const d = await api(`/threads/${encodeURIComponent(m.thread)}/messages/`, {
          method: "POST", body: JSON.stringify({ text: m.text, clientId: m.clientId }),
        });
        mergeMessage(d.message);
        upsertThread({ ...d.thread, local: false });
        C.markHttp(true);
        emit();
        return;
      } catch (e) {
        const retryable = !e || !e.status || e.status >= 500;
        if (!e || !e.status) C.markHttp(false);
        if (!retryable || attempt === 2 || !C.outbox.has(m.clientId)) {
          m.status = "failed"; m.error = e && e.status === 429 ? "rate" : "net";
          break;
        }
        await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
      }
    }
    emit();
  }

  /* ─── read receipts ──────────────────────────────────────────────────── */
  C.setVisibleThread = (id) => { C.visibleThread = id; if (id) C.markRead(id); };
  C.markRead = (id) => {
    const th = C.threads.get(id);
    if (!th || !C.me || (hasWindow && document.visibilityState !== "visible")) return;
    const b = C.msgs.get(id);
    const lastOther = b ? [...b.list].reverse().find((m) => m.id && m.from !== C.me) : null;
    const upTo = Math.max(th.lastMessageId || 0, lastOther ? lastOther.id : 0);
    if (!th.unread && (th.myReadId || 0) >= upTo) return;
    upsertThread({ id, unread: 0, myReadId: Math.max(th.myReadId || 0, upTo) });
    emit();
    if (C.ws && C.ws.readyState === 1) C.ws.send(JSON.stringify({ type: "read", thread: id, upTo }));
    else api(`/threads/${encodeURIComponent(id)}/read/`, { method: "POST", body: JSON.stringify({ upTo }) }).catch(() => {});
  };
  C.typingPing = (id) => { if (C.ws && C.ws.readyState === 1) C.ws.send(JSON.stringify({ type: "typing", thread: id })); };

  /* ─── socket lifecycle ───────────────────────────────────────────────── */
  function handle(ev) {
    switch (ev.type) {
      case "hello":
        C.helloUnread = ev.unread || 0;
        C.syncThreads();
        for (const id of C.msgs.keys()) catchUp(id);
        for (const m of C.outbox.values()) transmit(m);
        break;
      case "pong": clearTimeout(C.timers.pong); break;
      case "ack":
        mergeMessage(ev.message);
        if (ev.thread) upsertThread({ ...ev.thread, local: false });
        break;
      case "message": {
        const m = ev.message; const known = C.threads.has(m.thread);
        const th = upsertThread({ ...ev.thread, local: false });
        const b = C.msgs.get(m.thread);
        if (b && b.loaded) mergeMessage(m);
        else if (m.clientId) C.outbox.delete(m.clientId);
        if (m.from !== C.me) {
          C.typing.delete(m.thread);
          if (C.visibleThread === m.thread) C.markRead(m.thread);
          // a brand-new conversation: load names/listing first so the alert can say who wrote
          if (!known) C.fetchThread(m.thread).then(() => { if (C.onIncoming) C.onIncoming(m, C.threads.get(m.thread)); });
          else if (C.onIncoming) C.onIncoming(m, th);
        } else if (!known) C.fetchThread(m.thread);
        break;
      }
      case "read": {
        const th = C.threads.get(ev.thread);
        if (!th) break;
        if (ev.reader === C.me) upsertThread({ id: th.id, unread: 0, myReadId: Math.max(th.myReadId || 0, ev.readId) });
        else upsertThread({ id: th.id, peerReadId: Math.max(th.peerReadId || 0, ev.readId) });
        break;
      }
      case "typing":
        C.typing.set(ev.thread, Date.now() + 4000);
        clearTimeout(C.timers["ty" + ev.thread]);
        C.timers["ty" + ev.thread] = setTimeout(emit, 4100);
        break;
      case "error":
        if (ev.clientId && C.outbox.has(ev.clientId)) {
          const m = C.outbox.get(ev.clientId);
          clearTimeout(C.timers["ack" + ev.clientId]);
          m.status = "failed"; m.error = ev.code === "rate_limited" ? "rate" : "server";
          C.outbox.delete(ev.clientId);
        }
        break;
      default:
        // other live events (e.g. "room_request" for group admins) are handed to the app
        if (typeof window !== "undefined" && ev && ev.type) window.dispatchEvent(new CustomEvent("ac-live", { detail: ev }));
        return;
    }
    emit();
  }

  function setStatus(s) { if (C.status !== s) { C.status = s; emit(); } }
  function connect() {
    if (!hasWindow || !C.me || C.ws) return;
    let ws;
    try { ws = new WebSocket(realtimeUrl(apiBase)); } catch (e) { return scheduleReconnect(); }
    C.ws = ws; setStatus("connecting");
    // A proxy that swallows the upgrade leaves the socket "connecting" forever.
    clearTimeout(C.timers.open);
    C.timers.open = setTimeout(() => { if (ws.readyState === 0) { try { ws.close(); } catch (e) {} if (C.ws === ws) { C.ws = null; setStatus("offline"); scheduleReconnect(); } } }, 10000);
    ws.onopen = () => {
      clearTimeout(C.timers.open);
      C.attempts = 0; C.everConnected = true; setStatus("online");
      clearInterval(C.timers.ping);
      C.timers.ping = setInterval(() => {
        if (ws.readyState !== 1) return;
        ws.send('{"type":"ping"}');
        clearTimeout(C.timers.pong);
        C.timers.pong = setTimeout(() => { try { ws.close(4000, "pong timeout"); } catch (e) {} }, PONG_TIMEOUT);
      }, PING_EVERY);
    };
    ws.onmessage = (e) => { try { handle(JSON.parse(e.data)); } catch (err) { /* ignore malformed frame */ } };
    ws.onclose = (e) => {
      clearInterval(C.timers.ping); clearTimeout(C.timers.pong);
      if (C.ws === ws) C.ws = null;
      setStatus("offline");
      if (e.code === 4401) return; // signed out
      if (C.me) scheduleReconnect();
    };
    ws.onerror = () => { /* onclose follows */ };
  }
  function scheduleReconnect() {
    clearTimeout(C.timers.reconnect);
    const fallback = !C.everConnected && C.attempts >= 3;
    const base = fallback ? Math.min(300000, 120000 * (C.attempts - 2)) : Math.min(30000, 1000 * 2 ** Math.min(C.attempts, 5));
    const delay = base * (0.6 + Math.random() * 0.8);
    C.attempts += 1;
    C.timers.reconnect = setTimeout(connect, delay);
  }
  function pollWhileOffline() {
    clearInterval(C.timers.poll);
    let tick = 0;
    C.timers.poll = setInterval(() => {
      if (!C.me || C.status === "online" || document.visibilityState !== "visible") return;
      tick += 1;
      if (C.visibleThread) catchUp(C.visibleThread);
      if (tick % Math.round(POLL_OFFLINE / POLL_CHAT) === 0) C.syncThreads();
    }, POLL_CHAT);
  }
  const onWake = () => {
    if (!C.me || document.visibilityState !== "visible") return;
    if (!C.ws) { C.attempts = 0; clearTimeout(C.timers.reconnect); connect(); }
    if (C.visibleThread) C.markRead(C.visibleThread);
  };

  C.setUser = (key) => {
    if (key === C.me) return;
    C.stop();
    C.me = key || null;
    C.threads.clear(); C.msgs.clear(); C.outbox.clear(); C.typing.clear();
    C.threadsLoaded = false; C.helloUnread = 0;
    if (C.me && hasWindow) {
      connect(); pollWhileOffline(); C.syncThreads();
      window.addEventListener("online", onWake);
      window.addEventListener("focus", onWake);
      document.addEventListener("visibilitychange", onWake);
    }
    emit();
  };
  C.stop = () => {
    Object.values(C.timers).forEach((t) => { clearTimeout(t); clearInterval(t); });
    C.timers = {};
    if (C.ws) { const w = C.ws; C.ws = null; try { w.close(1000); } catch (e) {} }
    if (hasWindow) {
      window.removeEventListener("online", onWake);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    }
    C.status = "offline";
  };
  return C;
}
